import React, { useEffect, useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import hrLocale from "@fullcalendar/core/locales/hr";
import { toast } from "react-toastify";
import api from "common/api";
import { useAuth } from "authContext";

interface ISelectOption {
  value: string;
  label: string;
}

function groupTeachersBySubject(teachersList: any[]) {
  const subjectsGroup: any = {};

  teachersList.forEach((teacher) => {
    if (teacher.lesson_profile?.subjects?.length > 0) {
      teacher.lesson_profile.subjects.forEach((subject: any) => {
        if (!subjectsGroup[subject.name]) {
          subjectsGroup[subject.name] = [teacher];
        } else {
          subjectsGroup[subject.name].push(teacher);
        }
      });
    } else {
      if (!subjectsGroup["Nema predmeta"]) {
        subjectsGroup["Nema predmeta"] = [teacher];
      } else {
        subjectsGroup["Nema predmeta"].push(teacher);
      }
    }
  });

  return subjectsGroup;
}

function formatGroupedTeachersOptions(groupedTeachers: any) {
  return Object.keys(groupedTeachers).map((subject) => ({
    label: subject,
    options: groupedTeachers[subject].map((teacher: any) => ({
      label: `${teacher.first_name} ${teacher.last_name}`,
      value: teacher.user_id,
    })),
  }));
}

const findTeacherOption = (teacherId: string, options: any[]) => {
  for (let i = 0; i < options.length; i++) {
    const foundOption = options[i].options.find(
      (option: any) => option.value === teacherId,
    );
    if (foundOption) {
      return foundOption;
    }
  }
  return null;
};

const CalendarPage = () => {
  const [teachers, setTeachers] = useState<any[]>([]);
  const [groupedTeachersOptions, setGroupedTeachersOptions] = useState<any>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<any | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const { userProfile } = useAuth();
  const isTeacher = userProfile?.role === "TEACHER";

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const response = await api.get("/teachers/");

        const filteredTeachers = response.data.filter((teacher: any) => {
          return teacher.lesson_profile != null;
        });

        setTeachers(filteredTeachers);

        // Format teachers for the select component
        const groupedTeachers = groupTeachersBySubject(filteredTeachers);
        setGroupedTeachersOptions(
          formatGroupedTeachersOptions(groupedTeachers),
        );

        if (isTeacher && userProfile) {
          const teacherData = filteredTeachers.find(
            (inst: any) => inst.user_id === userProfile.user_id,
          );
          if (teacherData) {
            setSelectedTeacher(teacherData);
          }
        }
      } catch (error) {
        console.error(error);
        toast.error("Greška prilikom učitavanja nastavnika");
      }
    };
    fetchInstructors();
  }, [isTeacher, userProfile]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!selectedTeacher) {
        setEvents([]);
        return;
      }

      try {
        const teacherId = selectedTeacher.user_id;
        const response = await api.get(`/calendars/?teacher=${teacherId}`);
        setEvents(response.data);
      } catch (error) {
        console.error(error);
        toast.error("Error loading events");
      }
    };

    fetchEvents();
  }, [selectedTeacher]);

  const handleTeacherSelect = (selectedOption: ISelectOption | null) => {
    if (selectedOption) {
      const teacher = teachers.find(
        (inst) => inst.user_id === selectedOption.value,
      );
      setSelectedTeacher(teacher);
    } else {
      setSelectedTeacher(null);
    }
  };

  const handleDateSelect = async (selectInfo: any) => {
    if (!selectedTeacher && !isTeacher) {
      toast.error("Potrebno je odabrati nastavnika");
      selectInfo.view.calendar.unselect();
      return;
    }

    // Check if selection spans multiple days
    const startDate = new Date(selectInfo.startStr);
    const endDate = new Date(selectInfo.endStr);

    if (
      startDate.getDate() !== endDate.getDate() ||
      startDate.getMonth() !== endDate.getMonth() ||
      startDate.getFullYear() !== endDate.getFullYear()
    ) {
      toast.error("Nije moguće dodati termin koji traje više dana");
      selectInfo.view.calendar.unselect();
      return;
    }

    const newEvent = {
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      teacher: selectedTeacher ? selectedTeacher.user_id : userProfile?.user_id,
    };

    try {
      const response = await api.post("/calendars/", newEvent);
      const newEventData = {
        ...response.data,
        id: response.data.id,
        teacher: { user_id: response.data?.teacher },
      };
      setEvents((prev) => [...prev, newEventData]);
      toast.success("Termin dodan");
    } catch (error) {
      console.error(error);
      toast.error("Greška prilikom dodavanja termina");
    }
    selectInfo.view.calendar.unselect();
  };

  const handleEventDrop = async (dropInfo: any) => {
    const { event, oldEvent } = dropInfo;
    try {
      await updateEvent(event);
    } catch (error) {
      event.setStart(oldEvent.start);
      event.setEnd(oldEvent.end);
      toast.error("Greška prilikom ažuriranja termina");
    }
  };

  const handleEventResize = async (resizeInfo: any) => {
    const { event, oldEvent } = resizeInfo;
    try {
      await updateEvent(event);
    } catch (error) {
      event.setStart(oldEvent.start);
      event.setEnd(oldEvent.end);
      toast.error("Greška prilikom ažuriranja termina");
    }
  };

  const updateEvent = async (eventApi: any) => {
    const eventId = eventApi.id;
    const update = {
      start: eventApi.start?.toISOString(),
      end: eventApi.end?.toISOString(),
    };

    await api.put(`/calendars/${eventId}/`, update);

    setEvents((prevEvents) =>
      prevEvents.map((event) =>
        +event.id === +eventId
          ? {
              ...event,
              start: eventApi.start?.toISOString(),
              end: eventApi.end?.toISOString(),
            }
          : event,
      ),
    );

    toast.success("Termin ažuriran");
  };

  const handleEventClick = (clickInfo: any) => {
    const { event } = clickInfo;
    if (event.extendedProps.status === "AVAILABLE") {
      setSelectedEvent(event);
      setShowDeleteModal(true);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      await api.delete(`/calendars/${selectedEvent.id}/`);
      setEvents((prevEvents) =>
        prevEvents.filter((e) => +e.id !== +selectedEvent.id),
      );
      selectedEvent.remove();
      toast.success("Termin obrisan");
      setShowDeleteModal(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error(error);
      toast.error("Error deleting event");
    }
  };

  const showCalendar = isTeacher ? !!selectedTeacher : !!selectedTeacher;

  return (
    <Container>
      <h1>Kalendar - termini nastavnika</h1>
      <hr />
      {!isTeacher && (
        <Row className="mb-3">
          <Col>
            <Select
              options={groupedTeachersOptions}
              onChange={handleTeacherSelect}
              value={
                selectedTeacher
                  ? findTeacherOption(
                      selectedTeacher.user_id,
                      groupedTeachersOptions,
                    )
                  : null
              }
              placeholder="Odaberite nastavnika"
              isClearable
              menuPortalTarget={document.body}
              // zIndex to ensure the select menu is on top of the full calendar
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Col>
        </Row>
      )}
      {isTeacher && selectedTeacher && (
        <Row className="mb-3">
          <Col>
            <h4>
              Vaš kalendar - {selectedTeacher.first_name}{" "}
              {selectedTeacher.last_name}
            </h4>
          </Col>
        </Row>
      )}
      {showCalendar ? (
        <Row>
          <Col>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
              initialView="timeGridWeek"
              selectable={true}
              select={handleDateSelect}
              editable={true}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              eventClick={handleEventClick}
              headerToolbar={{
                start: "title",
                center: "",
                end: "today prev,next dayGridMonth,timeGridWeek,timeGridDay",
              }}
              locale={hrLocale}
              slotMinTime="07:00:00"
              slotMaxTime="23:00:00"
              events={events}
              selectMirror={true}
              selectOverlap={false}
              selectConstraint={{ startTime: "07:00", endTime: "23:00" }}
              allDaySlot={false}
              height="auto"
            />
          </Col>
        </Row>
      ) : (
        <Row>
          <Col className="text-center py-5">
            <h4>Odaberite nastavnika za prikaz kalendara</h4>
          </Col>
        </Row>
      )}

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Potvrda brisanja</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Jeste li sigurni da želite obrisati ovaj termin?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={() => setShowDeleteModal(false)}
          >
            Odustani
          </Button>
          <Button variant="primary" onClick={handleDeleteEvent}>
            Obriši
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CalendarPage;
